'use client'

import HeaderWithBackButton from '@/components/common/HeaderWithBackButton'
import { TERMS_CATEGORIES } from '@/constants/terms'
import { useTermsDataQuery } from '@/hooks/useContentfulQuery'
import { createTermsRenderOptions } from '@/utils/renderOption/termsRenderOption'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import type { Document } from '@contentful/rich-text-types'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const Terms = () => {
  const { t } = useTranslation('terms')

  const DEFAULT_CATEGORY = 'terms_of_service'
  const [selectedCategory, setSelectedCategory] = useState<string>(DEFAULT_CATEGORY)

  // NOTE: Avoid next/navigation hooks to keep page pre-renderable

  const handleCategoryClick = (category: string) => {
    if (!TERMS_CATEGORIES.includes(category)) return
    setSelectedCategory(category)
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      url.searchParams.set('tab', category)
      window.history.replaceState(null, '', url.toString())
    }
  }

  const { data, isPending } = useTermsDataQuery('terms', selectedCategory)

  useEffect(() => {
    const tab = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('tab') : null
    if (tab && TERMS_CATEGORIES.includes(tab)) {
      setSelectedCategory(tab)
    } else {
      setSelectedCategory(DEFAULT_CATEGORY)
    }
  }, [])

  return (
    <section className='pb-[calc(env(safe-area-inset-bottom)+80px)]'>
      <div className="sm:hidden">
        <HeaderWithBackButton />
      </div>
      <div className="hidden sm:flex mt-[128px] justify-center">
        <h2 className="font-pretendard-bold text-[62px] text-black dark:text-white">
          {t('terms_header')}
        </h2>
      </div>
      <div className="mx-auto px-5 sm:mt-[56px] mt-6">
        <div>
          <div className="flex gap-[14px] sm:justify-center overflow-x-auto overflow-y-hidden flex-nowrap [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
            {TERMS_CATEGORIES.map((category, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleCategoryClick(category)}
                className={`shrink-0 whitespace-nowrap text-[15px] ${selectedCategory === category
                  ? 'text-primary border-b-2 border-primary font-pretendard-bold'
                  : 'text-main-light-1 dark:text-main-dark-1 border-b-2 border-transparent'
                  }`}
              >
                {t(category)}
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-col mt-[25px] sm:mt-[42px] max-w-[640px] mx-auto">
          {!isPending ? (
            <div>
              <p className="font-pretendard-bold text-[15px] text-black dark:text-white">
                {data?.terms[0].title}
              </p>

              {/* 요약 */}
              {data?.terms[0]?.summary && (
                <div className="mt-[15px]">
                  {documentToReactComponents(
                    data.terms[0].summary as Document,
                    createTermsRenderOptions(data?.entriesById ?? {})
                  )}
                </div>
              )}

              {/* 본문 */}
              {data?.terms[0]?.body && (
                <div className="mt-[15px]">
                  {documentToReactComponents(
                    data.terms[0].body as Document,
                    createTermsRenderOptions(data?.entriesById ?? {})
                  )}
                </div>
              )}
            </div>
          ) : (
            <p>Loading...</p>
          )}
        </div>
      </div>
    </section>
  )
}

export default Terms
